import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Mountain } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from './LanguageToggle';

const Navbar = ({ scrolled }) => {
  const { language, localizedPath } = useLanguage();
  const tr = language === 'tr';
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [localScrolled, setLocalScrolled] = useState(scrolled || false);

  useEffect(() => {
    const handleScroll = () => setLocalScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isScrolled = scrolled || localScrolled;
  const onHome = location.pathname === '/' || location.pathname === '/ana-sayfa';

  // Section anchors only make sense on the home page. On inner pages they
  // should link back to /#section instead of pure #section (which would 404).
  const a = (hash) => (onHome ? hash : `/${hash}`);

  const navLinks = [
    { key: 'register', label: tr ? 'Kayıt'     : 'Register', href: a('#zones')    },
    { key: 'agenda',   label: tr ? 'Program'   : 'Agenda',   href: a('#agenda')   },
    { key: 'about',    label: tr ? 'Hakkında'  : 'About',    href: a('#about')    },
    { key: 'faq',      label: tr ? 'SSS'       : 'FAQ',      href: a('#faq')      },
  ];

  const loginPath    = localizedPath('login');
  const registerPath = localizedPath('register');

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
      isScrolled
        ? 'bg-white/95 backdrop-blur-xl border-b border-[rgba(10,58,90,0.08)] shadow-sm'
        : 'bg-white/70 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <div className="h-9 w-9 rounded-xl bg-[#0A3A5A] flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
              <Mountain className="h-5 w-5 text-[#6EE7B7]" strokeWidth={1.8} />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-playfair text-lg md:text-xl text-[#0A3A5A] font-medium">
                SummitFlow
              </span>
              <span className="text-[9px] uppercase tracking-[0.3em] text-[#4A5568] font-semibold mt-0.5">
                Global Summit
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(link => (
              <a
                key={link.key}
                href={link.href}
                data-testid={`nav-${link.key}`}
                className="px-4 py-2 text-sm text-[#4A5568] hover:text-[#0A3A5A] transition-colors font-medium rounded-lg hover:bg-[#0A3A5A]/5"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageToggle variant="pill" />
            <Link
              to={loginPath}
              data-testid="nav-login-button"
              className="px-5 py-2.5 text-sm text-[#0A3A5A] hover:bg-[#0A3A5A]/5 transition-colors font-semibold rounded-md"
            >
              {tr ? 'Giriş' : 'Login'}
            </Link>
            <Link
              to={registerPath}
              data-testid="nav-get-started-button"
              className="px-5 py-2.5 text-sm bg-[#0A3A5A] hover:bg-[#144D75] text-white rounded-md font-semibold transition-all"
            >
              {tr ? 'Kayıt Ol' : 'Register'}
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden text-[#0A3A5A] p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="nav-mobile-menu"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-[rgba(10,58,90,0.08)] px-4 py-4 space-y-1 shadow-lg">
          {navLinks.map(link => (
            <a
              key={link.key}
              href={link.href}
              className="block px-4 py-3 text-[#4A5568] hover:text-[#0A3A5A] hover:bg-[#0A3A5A]/5 rounded-xl transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <div className="pt-3 flex flex-col gap-2">
            <div className="flex justify-center pb-1"><LanguageToggle variant="pill" /></div>
            <Link to={loginPath}    className="block text-center py-3 text-[#0A3A5A] border border-[#0A3A5A]/20 rounded-md font-semibold">{tr ? 'Giriş' : 'Login'}</Link>
            <Link to={registerPath} className="block text-center py-3 bg-[#0A3A5A] text-white rounded-md font-semibold">{tr ? 'Kayıt Ol' : 'Register'}</Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
